import { useEffect, useState } from "react";
import {
  formDisplayName,
  type PatientAttentionTexts,
  type PatientGroup,
} from "@care-by-ella/contracts";
import {
  attentionAnswerAnchor,
  attentionHeading,
  parentAnswerLine,
  submittedAtSv,
} from "@/lib/attention-texts";

// TES-1001 (Ö-249) — "Information att uppmärksamma": every free text the
// patient wrote in a medical form, on the patient overview, so the doctor
// never has to open a whole form to find her own words (A-02, 2026-09-29).
//
// For each text: her full text unedited, the question, the answer that
// opened the follow-up, the readable form name, the submission date and time,
// and a link to the source answer. The text itself is the row, never a
// heading:
//   "En sektionsrubrik får aldrig ersätta patientens faktiska text."
//
// This is visibility, not severity: nothing here is a risk flag, and the
// styling stays neutral. A-02:
//   "All fritext ska alltså visas, men får inte automatiskt framställas som akut eller röd."
//
// The link opens the whole submission below the list and scrolls to her
// answer in it — every form, including the monthly check-in, which has no
// page of its own.

export function AttentionTextsPanel({
  attention,
  error,
  patientGroup,
}: {
  // null while the form-facts request is in flight or failed.
  attention: PatientAttentionTexts | null;
  error?: string | null;
  patientGroup?: PatientGroup | null;
}) {
  const [openSource, setOpenSource] = useState<string | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const formName = (key: string) => formDisplayName(key, patientGroup);

  // Scroll once the source view is rendered, not before.
  useEffect(() => {
    if (!target) return;
    document.getElementById(target)?.scrollIntoView({ block: "center" });
  }, [target, openSource]);

  const items = attention?.items ?? [];
  const source = attention?.sources.find((s) => s.submissionId === openSource);

  return (
    <section
      aria-label="Information att uppmärksamma"
      className="rounded-md border border-surfaceMuted bg-surface p-md"
    >
      <h2 className="mb-sm font-display text-lg">
        {attentionHeading(items.length)}
      </h2>
      {error ? (
        <p className="mb-sm text-sm text-danger">{error}</p>
      ) : attention === null ? (
        <p className="mb-sm text-sm text-textSecondary">
          Hämtar patientens fritextsvar…
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-textSecondary">
          Patienten har inte skrivit någon fritext i formulären.
        </p>
      ) : (
        <ul className="flex flex-col gap-md">
          {items.map((item) => {
            const anchor = attentionAnswerAnchor(
              item.submissionId,
              item.fieldKey,
            );
            const parent = parentAnswerLine(item);
            return (
              <li
                key={anchor}
                className="border-l-2 border-peach pl-sm text-sm"
              >
                <p className="text-xs text-textSecondary">{item.question}</p>
                {parent ? (
                  <p className="text-xs text-textSecondary">
                    Följdfråga till: {parent}
                  </p>
                ) : null}
                <p className="mt-xs whitespace-pre-wrap break-words font-medium text-textPrimary">
                  {item.text}
                </p>
                <p className="mt-xs text-xs text-textSecondary">
                  {formName(item.templateKey)} · inskickat{" "}
                  {submittedAtSv(item.submittedAt)} ·{" "}
                  <a
                    href={`#${anchor}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenSource(item.submissionId);
                      setTarget(anchor);
                    }}
                    className="text-brand hover:underline"
                  >
                    Öppna källsvaret
                  </a>
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {source ? (
        <div className="mt-md rounded-md bg-surfaceMuted/40 p-sm text-sm">
          <div className="mb-xs flex items-start justify-between gap-sm">
            <p className="text-xs text-textSecondary">
              Källsvar: {formName(source.templateKey)} v
              {source.templateVersion} · inskickat{" "}
              {submittedAtSv(source.submittedAt)}
            </p>
            <button
              type="button"
              onClick={() => {
                setOpenSource(null);
                setTarget(null);
              }}
              className="text-xs text-textSecondary hover:text-textPrimary"
            >
              Stäng
            </button>
          </div>
          <dl className="flex flex-col gap-xs">
            {source.answers.map((a) => {
              const anchor = attentionAnswerAnchor(
                source.submissionId,
                a.fieldKey,
              );
              return (
                <div
                  key={anchor}
                  id={anchor}
                  className={
                    anchor === target ? "rounded-sm bg-peach/40 p-xs" : ""
                  }
                >
                  <dt className="text-xs text-textSecondary">
                    {a.label ?? a.fieldKey}
                  </dt>
                  <dd className="whitespace-pre-wrap break-words text-textPrimary">
                    {a.value}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      ) : null}
    </section>
  );
}
